/**
 * Determine whether the current user can adjust the time of a given session,
 * and provide a reason when they cannot.
 *
 * Rules:
 * - 1:1 sessions cannot be adjusted if verified. Only admins or the assigned provider can adjust.
 * - Group sessions cannot be adjusted if deleted or verified. Only admins or assigned providers can adjust, and group sessions must be enabled in settings.
 * - Hidden/deleted sessions that are not in the fetched list will not reach this function (getSelectedSession returns null), so the link is simply not visible.
 * - If the session object is null or invalid, adjusting time is not allowed and the adjust time option should be hidden.
 *
 * @param {Object|null} session  - Session object with `_type` ('1:1' | 'group')
 * @param {Object}      user     - Current user from Vuex auth state
 * @param {Object|null} publicSettings - Public settings from Vuex settings state
 * @returns {{ allowed: boolean, reason: string, visible: boolean }}
 */
export function getAdjustTimeStatus(session, user, publicSettings) {
  if (!session) return { allowed: false, reason: "", visible: false };

  const isAdmin = user.isAdmin || user.isManagingAdmin;
  const blocked = (reason) => ({ allowed: false, reason, visible: true });
  const allowed = { allowed: true, reason: "", visible: true };

  if (session._type === "1:1") {
    if (session.status === "Verified")
      return blocked("Cannot adjust time of a verified session");
    if (isAdmin) return allowed;
    const providerId = session.user_1?.id ?? session.user_1;
    if (user.isProvider && user.id == providerId) return allowed;
    return blocked("You are not assigned to this session");
  }

  if (session._type === "group") {
    if (session.deleted_at)
      return blocked("Cannot adjust time of a deleted session");
    if (publicSettings?.GROUP_SESSIONS_ENABLED === false)
      return blocked("Group sessions feature is disabled");
    if (session.status === "VERIFIED")
      return blocked("Cannot adjust time of a verified session");
    if (isAdmin) return allowed;
    if (
      user.isProvider &&
      (session.providers || []).some((p) => p.id === user.id)
    )
      return allowed;
    return blocked("You are not assigned to this session");
  }

  return { allowed: false, reason: "", visible: false };
}
